// The cell at work — row G, "three signs, one motion" (`design/toile_wall/WorkingStates.dc.html`,
// row G; see `WorkingCell.tsx`'s own header for why claimed is not a separate paint). One
// definition, rendered inside `Tile.module.scss`'s `.tile` and inside `Column.module.scss`'s /
// `Sheet.module.scss`'s `.result` — both callers give their container `position: relative` and
// `container-type: inline-size` so the numeral's `cqw` sizing (below) scales with whichever box it
// is standing in, a 72px board cell or the armed frame's own fitted box, rather than the viewport.
// The house's `--tl-*` tokens (`tokens.ts`).

// The breath — the only mark asking "is it alive?". Queued AND claimed/rendering share it: it
// stops the instant a frame lands (breath and a frame never coexist). 105deg / 400% size / 2.2s
// ease-in-out, no animation-delay anywhere in this file — every breathing cell stays in phase by
// construction, not by a shared clock.
.breath {
    position: absolute;
    inset: 0;
    display: block;
    background: linear-gradient(105deg, var(--tl-surface) 0%, var(--tl-border-strong) 50%, var(--tl-surface) 100%);
    background-size: 400% 100%;
    animation: breathSlide 2.2s ease-in-out infinite;
}

@keyframes breathSlide {
    from {
        background-position: 200% 0;
    }
    to {
        background-position: -200% 0;
    }
}

// The place — the row's only text, sized as an edition number rather than a caption (row G's own
// wording). Centred (flex-centered over the full box). 23.6cqw of the container's own inline size
// renders 17px on a 72px board cell — the concrete point row G measured against, "roughly a
// quarter of the cell width" — and follows the container's own width at any other size (a ~110px
// phone cell, the armed frame's own fitted box) via `container-type: inline-size` on the caller's
// containing element, so it stays proportionate without a breakpoint. `--tl-dim` at any other
// place; `--tl-text` at 1, the row about to be claimed or already on the machine (`.numeralFirst`).
.numeral {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--tl-font-label);
    font-size: 23.6cqw;
    letter-spacing: 0.04em;
    line-height: 1;
    color: var(--tl-dim);
    pointer-events: none;
}

.numeralFirst {
    color: var(--tl-text);
}

// The frame — a picture emerging is its own progress (row G's own reasoning): the estimate as the
// service made it, no filter, no blur, no scale, no per-frame ramp.
.frame {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

// The wall's motion is decoration: with reduced motion asked for, the breath parks flat and a
// picture simply appears. The numeral and the frames carry no animation of their own to park.
// The preference targets motion (travel/slide/parallax), not a slow change of tone in place — a
// queued/claimed/rendering cell must never read as a still, "nothing happening" surface (operator
// amendment 2026-09-12). The sliding gradient is dropped outright; a flat background-color pulse
// between the same two tones takes over, slow enough (4.4s) to carry no motion-sickness risk. No
// animation-delay here either — every reduced-motion cell still pulses in the same phase.
@keyframes breathColorPulse {
    from {
        background-color: var(--tl-surface);
    }
    to {
        background-color: var(--tl-border-strong);
    }
}

@media (prefers-reduced-motion: reduce) {
    .breath {
        animation: breathColorPulse 4.4s ease-in-out infinite alternate;
        // Strips the gradient (an opaque image layer paints over background-color) so the pulse
        // is the only thing visible — background-color alone would sit invisibly behind it.
        background-image: none;
        background-color: var(--tl-surface);
    }
}
